import * as Common from '@piccolohealth/pbs-common';
import { LabelType } from '@piccolohealth/pbs-common';
import {
  ChakraV3,
  FloatingPopover,
  MultiSelect,
  type MultiSelectOption,
  NumberedBadge,
  type OnChangeRequest,
  RangeDatepicker,
} from '@piccolohealth/ui';
import { P } from '@piccolohealth/util';
import React from 'react';
import { FaCaretDown, FaFilter } from 'react-icons/fa';

import { FormStack } from '../../components/forms/FormStack';
import { useIncidentCategoriesQuery } from '../../graphql/hooks/useIncidentCategoriesQuery';
import { useLabelsQuery } from '../../graphql/hooks/useLabelsQuery';
import { useOrganizationQuery } from '../../graphql/hooks/useOrganizationQuery';
import { useAppContext } from '../../hooks/useAppContext';
import type { IncidentsFilter } from '../../hooks/useIncidentsFilter';
import { IncidentSeverityTag, incidentSeverityDetails } from './IncidentSeverityTag';

interface Props {
  filter: IncidentsFilter;
}

const IncidentDateFilterControl = (props: Props) => {
  return (
    <RangeDatepicker
      startDate={props.filter.startDateFilter ?? null}
      endDate={props.filter.endDateFilter ?? null}
      onStartDateChange={(date) => props.filter.onStartDateFilter(date ?? undefined)}
      onEndDateChange={(date) => props.filter.onEndDateFilter(date ?? undefined)}
      size='sm'
    />
  );
};

const IncidentCategoryFilterControl = (props: Props) => {
  const { organization } = useAppContext();

  const { isLoading, data } = useIncidentCategoriesQuery({
    organizationId: organization.id,
  });

  const options: MultiSelectOption<Common.IncidentCategory>[] = React.useMemo(() => {
    const incidentCategories = (data?.organization?.incidentCategories ??
      []) as Common.IncidentCategory[];

    return incidentCategories.map((category) => {
      return {
        label: category.name,
        value: category.id,
        raw: category,
        color: category.color,
      };
    });
  }, [data?.organization?.incidentCategories]);

  const selectedValues: MultiSelectOption<Common.IncidentCategory>[] = React.useMemo(() => {
    return (props.filter.categoryFilter ?? []).flatMap((category) => {
      return options.find((o) => P.isEqual(o.raw.id, category)) ?? [];
    });
  }, [props.filter, options]);

  const onChange = React.useCallback(
    (req: OnChangeRequest<Common.IncidentCategory>) => {
      props.filter.onCategoryFilter(req.values.map(({ raw }) => raw.id));
    },
    [props.filter],
  );

  return (
    <MultiSelect
      isLoading={isLoading}
      selectedValues={selectedValues}
      options={options}
      onChange={onChange}
      size='sm'
      placeholder='All categories'
      optionVariant='tag'
    />
  );
};

const IncidentLocationFilterControl = (props: Props) => {
  const { organization } = useAppContext();

  const { isLoading, data } = useOrganizationQuery({
    organizationId: organization.id,
  });

  const options: MultiSelectOption<Common.Location>[] = React.useMemo(() => {
    const locations = (data?.organization?.locations as Common.Location[]) ?? [];
    return locations.map((location) => {
      return {
        label: location.name,
        value: location.id,
        raw: location,
        color: location.color,
      };
    });
  }, [data?.organization?.locations]);

  const selectedValues: MultiSelectOption<Common.Location>[] = React.useMemo(() => {
    return (props.filter.locationFilter ?? []).flatMap((location) => {
      return options.find((o) => P.isEqual(o.raw.id, location)) ?? [];
    });
  }, [props.filter, options]);

  const onChange = React.useCallback(
    (req: OnChangeRequest<Common.Location>) => {
      props.filter.onLocationFilter(req.values.map(({ raw }) => raw.id));
    },
    [props.filter],
  );

  return (
    <MultiSelect
      isLoading={isLoading}
      selectedValues={selectedValues}
      options={options}
      onChange={onChange}
      size='sm'
      placeholder='All locations'
      optionVariant='tag'
    />
  );
};

const IncidentSeverityFilterControl = (props: Props) => {
  const options: MultiSelectOption<Common.IncidentSeverity>[] = React.useMemo(
    () =>
      Object.values(Common.IncidentSeverity).map((severity) => {
        const [color, label] = incidentSeverityDetails(severity);

        return {
          value: severity,
          raw: severity,
          label,
          color,
        };
      }),
    [],
  );

  const selectedValues: MultiSelectOption<Common.IncidentSeverity>[] = React.useMemo(() => {
    return (props.filter.severityFilter ?? []).flatMap((severity) => {
      return options.find((o) => P.isEqual(o.raw, severity)) ?? [];
    });
  }, [props.filter, options]);

  const onChange = React.useCallback(
    (req: OnChangeRequest<Common.IncidentSeverity>) => {
      props.filter.onSeverityFilter(req.values.map(({ raw }) => raw));
    },
    [props.filter],
  );

  return (
    <MultiSelect
      selectedValues={selectedValues}
      options={options}
      onChange={onChange}
      size='sm'
      placeholder='All severities'
      optionVariant='tag'
      components={{
        Option: ({ option }) => {
          return <IncidentSeverityTag severity={option.raw} size='sm' />;
        },
      }}
    />
  );
};

const IncidentStatusFilterControl = (props: Props) => {
  const options: MultiSelectOption<Common.IncidentStatus>[] = React.useMemo(
    () => [
      {
        label: 'Awaiting Review',
        value: Common.IncidentStatus.AwaitingReview,
        raw: Common.IncidentStatus.AwaitingReview,
        color: 'yellow',
      },
      {
        label: 'Reviewed',
        value: Common.IncidentStatus.Reviewed,
        raw: Common.IncidentStatus.Reviewed,
        color: 'green',
      },
    ],
    [],
  );

  const selectedValues: MultiSelectOption<Common.IncidentStatus>[] = React.useMemo(() => {
    return (props.filter.statusFilter ?? []).flatMap((status) => {
      return options.find((o) => P.isEqual(o.raw, status)) ?? [];
    });
  }, [props.filter, options]);

  const onChange = React.useCallback(
    (req: OnChangeRequest<Common.IncidentStatus>) => {
      props.filter.onStatusFilter(req.values.map(({ raw }) => raw));
    },
    [props.filter],
  );

  return (
    <MultiSelect
      selectedValues={selectedValues}
      options={options}
      onChange={onChange}
      size='sm'
      placeholder='All statuses'
      optionVariant='tag'
    />
  );
};

const IncidentLabelFilterControl = (props: Props) => {
  const { organization } = useAppContext();
  const { data, isLoading } = useLabelsQuery({
    organizationId: organization.id,
    type: LabelType.Incident,
  });

  const options: MultiSelectOption<Common.Label>[] = React.useMemo(() => {
    const labels = (data?.organization?.labels as Common.Label[]) ?? [];
    return labels.map((label) => {
      return {
        label: label.name,
        value: label.id,
        raw: label,
        color: label.color,
      };
    });
  }, [data?.organization?.labels]);

  const selectedValues: MultiSelectOption<Common.Label>[] = React.useMemo(() => {
    return (props.filter.labelFilter ?? []).flatMap((label) => {
      return options.find((o) => P.isEqual(o.raw.id, label)) ?? [];
    });
  }, [props.filter, options]);

  const onChange = React.useCallback(
    (req: OnChangeRequest<Common.Label>) => {
      props.filter.onLabelFilter(req.values.map(({ raw }) => raw.id));
    },
    [props.filter],
  );

  return (
    <MultiSelect
      isLoading={isLoading}
      selectedValues={selectedValues}
      options={options}
      onChange={onChange}
      size='sm'
      placeholder='All labels'
      optionVariant='tag'
    />
  );
};

export const IncidentsFilterControls = (props: Props) => {
  return (
    <FormStack orientation='horizontal'>
      <FloatingPopover
        render={() => (
          <ChakraV3.Stack bg='white' gap={0} w='xs' shadow='2xl' rounded='md'>
            <ChakraV3.HStack py={2} px={4} borderBottomWidth='1px'>
              <ChakraV3.Text fontWeight='semibold'>Filters</ChakraV3.Text>
              <ChakraV3.Spacer />
              <NumberedBadge count={props.filter.activeFilterCount} />
            </ChakraV3.HStack>
            <FormStack py={4} px={4} orientation='vertical'>
              <ChakraV3.FormField label='Date'>
                <IncidentDateFilterControl filter={props.filter} />
              </ChakraV3.FormField>

              <ChakraV3.FormField label='Category'>
                <IncidentCategoryFilterControl filter={props.filter} />
              </ChakraV3.FormField>

              <ChakraV3.FormField label='Location'>
                <IncidentLocationFilterControl filter={props.filter} />
              </ChakraV3.FormField>

              <ChakraV3.FormField label='Severity'>
                <IncidentSeverityFilterControl filter={props.filter} />
              </ChakraV3.FormField>

              <ChakraV3.FormField label='Status'>
                <IncidentStatusFilterControl filter={props.filter} />
              </ChakraV3.FormField>

              <ChakraV3.FormField label='Labels'>
                <IncidentLabelFilterControl filter={props.filter} />
              </ChakraV3.FormField>

              <ChakraV3.Separator />

              <ChakraV3.Button
                onClick={props.filter.reset}
                w='fit-content'
                size='sm'
                variant='outline'
                colorPalette='brand'
                disabled={props.filter.activeFilterCount === 0}
              >
                Reset
              </ChakraV3.Button>
            </FormStack>
          </ChakraV3.Stack>
        )}
      >
        <ChakraV3.Button size='sm' variant='outline' lineHeight='unset'>
          <ChakraV3.HStack>
            <FaFilter />
            Filter
            <ChakraV3.Stack w='28px'>
              <NumberedBadge count={props.filter.activeFilterCount} />
            </ChakraV3.Stack>
            <FaCaretDown />
          </ChakraV3.HStack>
        </ChakraV3.Button>
      </FloatingPopover>
    </FormStack>
  );
};
